import React, { useEffect, useState } from 'react';
import { useParams, useNavigate, useOutletContext } from 'react-router-dom';
import { supabase } from '../../lib/supabase';
import { isDemoTournament, getDemoTeams, getDemoMatches } from '../../data/demoData';
import { Loader2, Shirt, Clock, Trophy, Calendar, MapPin, AlertCircle, RefreshCw, Gamepad2, Zap, Target, Activity, Award, Bell, BellRing, Shield } from 'lucide-react';
import { usePushNotifications } from '../../hooks/usePushNotifications';

const PublicMyTeamPage = () => {
    const { id: tournamentId } = useParams();
    const navigate = useNavigate();
    const outletContext = useOutletContext();
    const isEsports = outletContext?.isEsports || false;

    const [loading, setLoading] = useState(true);
    const [teams, setTeams] = useState([]);
    const [selectedTeamId, setSelectedTeamId] = useState(() => localStorage.getItem('propoule_fav_team'));
    const [teamData, setTeamData] = useState(null); // { team, nextMatch, ranking }
    const { isSupported, isSubscribed, subscribe, loading: pushLoading } = usePushNotifications();

    useEffect(() => {
        fetchTeams();
    }, [tournamentId]);

    useEffect(() => {
        if (selectedTeamId) {
            localStorage.setItem('propoule_fav_team', selectedTeamId);
            fetchTeamDetails(selectedTeamId);
        } else {
            setTeamData(null);
        }
    }, [selectedTeamId]);

    const fetchTeams = async () => {
        if (isDemoTournament(tournamentId)) {
            setTeams(getDemoTeams(tournamentId));
            setLoading(false);
            return;
        }

        const { data } = await supabase
            .from('teams')
            .select('id, name, poule_id, poule:poules(name), logo_url')
            .eq('tournament_id', tournamentId)
            .order('name');
        setTeams(data || []);
        setLoading(false);
    };

    const fetchTeamDetails = async (teamId) => {
        setLoading(true);

        if (isDemoTournament(tournamentId)) {
            const dTeams = getDemoTeams(tournamentId);
            const dMatches = getDemoMatches(tournamentId);
            const team = dTeams.find(t => t.id === teamId);
            const matches = dMatches.filter(m => m.home_team_id === teamId || m.away_team_id === teamId);

            // Sort matches
            matches.sort((a, b) => new Date(a.match_date || 0) - new Date(b.match_date || 0));

            const nextMatch = matches.find(m => m.status !== 'completed' && m.score_home === null) || null;
            const lastMatch = [...matches].reverse().find(m => m.score_home !== null) || null;

            setTeamData({
                team,
                nextMatch,
                lastMatch,
                totalMatches: matches.length,
                playedMatches: matches.filter(m => m.score_home !== null).length,
                players: [
                    { id: '1', name: 'Hans Sama', reaction_speed: 178, biometrics: { mechanical: 94, speed: 89, brain: 85, focus: 92, chemistry: 90 } },
                    { id: '2', name: 'Upset', reaction_speed: 195, biometrics: { mechanical: 88, speed: 82, brain: 85, focus: 80, chemistry: 85 } },
                    { id: '3', name: 'zampi', reaction_speed: 210, biometrics: { mechanical: 82, speed: 78, brain: 90, focus: 85, chemistry: 88 } }
                ]
            });
            setLoading(false);
            return;
        }

        try {
            // 1. Get Team Info
            let team = teams.find(t => t.id === teamId);
            if (!team) {
                const { data: teamData } = await supabase
                    .from('teams')
                    .select('id, name, poule_id, poule:poules(name), logo_url')
                    .eq('id', teamId)
                    .single();
                team = teamData;
            }

            // 2. Get Matches for this team
            const { data: matches } = await supabase
                .from('matches')
                .select(`
                    id, start_time, match_date, field, score_home, score_away, status,
                    home_team:teams!matches_home_team_id_fkey(name),
                    away_team:teams!matches_away_team_id_fkey(name),
                    home_team_id, away_team_id
                `)
                .or(`home_team_id.eq.${teamId},away_team_id.eq.${teamId}`)
                .eq('tournament_id', tournamentId)
                .order('match_date')
                .order('start_time');

            // 3. Get Players for this team
            const { data: players } = await supabase
                .from('players')
                .select('*')
                .eq('team_id', teamId)
                .order('name');

            // Find Next Match (first match that is not finished)
            const nextMatch = matches?.find(m => m.status !== 'completed' && m.score_home === null) || null;
            const lastMatch = [...(matches || [])].reverse().find(m => m.score_home !== null) || null;

            setTeamData({
                team,
                nextMatch,
                lastMatch,
                totalMatches: matches?.length || 0,
                playedMatches: matches?.filter(m => m.score_home !== null).length || 0,
                players: players || []
            });

        } catch (err) {
            console.error(err);
        } finally {
            setLoading(false);
        }
    };

    if (loading && !teams.length) return <div className="flex justify-center p-8"><Loader2 className="animate-spin text-white" /></div>;

    // View: Team Selection
    if (!selectedTeamId || !teamData) {
        return (
            <div className="p-4 space-y-4 max-w-xl mx-auto text-center font-sans">
                <div className={`backdrop-blur rounded-2xl p-8 shadow-xl border transition-all duration-300 ${
                    isEsports 
                        ? 'bg-[#0a0a14]/90 border-purple-500/20 text-white shadow-[0_0_35px_rgba(168,85,247,0.15)]' 
                        : 'bg-white/90 border-white/20 text-gray-800'
                }`}>
                    <div className={`mx-auto w-20 h-20 rounded-full flex items-center justify-center mb-4 transition-colors ${
                        isEsports 
                            ? 'bg-purple-950/60 text-cyan-400 border border-purple-500/20 shadow-[0_0_15px_rgba(168,85,247,0.2)]' 
                            : 'bg-indigo-100 text-indigo-600'
                    }`}>
                        <Shirt size={40} className={isEsports ? 'drop-shadow-[0_0_5px_rgba(6,182,212,0.5)]' : ''} />
                    </div>
                    <h2 className={`text-2xl font-black mb-2 tracking-tight ${isEsports ? 'text-white' : 'text-gray-900'}`}>Kies jouw Team</h2>
                    <p className={`mb-6 text-sm ${isEsports ? 'text-slate-400' : 'text-gray-500'}`}>Selecteer je team om je persoonlijke dashboard te zien.</p>

                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-2 text-left">
                        {teams.map(team => (
                            <button
                                key={team.id}
                                onClick={() => setSelectedTeamId(team.id)}
                                className={`p-3 rounded-xl border transition-all flex items-center gap-3 group ${
                                    isEsports 
                                        ? 'bg-purple-950/20 hover:bg-purple-500/10 border-purple-500/20 text-slate-200 hover:text-white shadow-[inset_0_0_15px_rgba(168,85,247,0.05)]' 
                                        : 'bg-gray-50 hover:bg-indigo-50 border-gray-100 hover:border-indigo-200 text-gray-700 hover:text-indigo-700'
                                }`}
                            >
                                <div className={`w-8 h-8 rounded-full shadow-sm flex items-center justify-center text-[10px] font-black group-hover:scale-110 transition-transform ${
                                    isEsports 
                                        ? 'bg-purple-950 border border-purple-500/20 text-cyan-400 font-mono' 
                                        : 'bg-white text-gray-400 group-hover:text-indigo-500'
                                }`}>
                                    {team.name.charAt(0)}
                                </div>
                                <span className="truncate">{team.name}</span>
                            </button>
                        ))}
                    </div>
                </div>
            </div>
        );
    }

    // View: Team Dashboard
    const { team, nextMatch, lastMatch } = teamData;

    return (
        <div className="p-4 space-y-6 max-w-2xl mx-auto font-sans">
            {/* Header */}
            <div className={`rounded-2xl p-6 shadow-xl relative overflow-hidden transition-all duration-300 border ${
                isEsports 
                    ? 'bg-gradient-to-r from-[#120826] to-[#041e30] border-purple-500/30 text-white shadow-[0_0_30px_rgba(168,85,247,0.2)]' 
                    : 'bg-gradient-to-r from-indigo-600 to-purple-600 border-transparent text-white'
            }`}>
                <div className="absolute top-0 right-0 opacity-10 pointer-events-none overflow-hidden w-full h-full">
                    {team?.logo_url ? (
                        <img src={team.logo_url} className="absolute -right-4 -top-4 w-48 h-48 object-contain blur-sm" alt="" />
                    ) : (
                        <div className="absolute -right-4 -top-4"><Shirt size={150} /></div>
                    )}
                </div>
                <div className="relative z-10">
                    <div className="flex justify-between items-start gap-4">
                        <div className="flex-1">
                            <div className={`text-xs font-bold uppercase tracking-wider mb-1.5 flex items-center gap-2 ${
                                isEsports ? 'text-purple-300' : 'text-indigo-200'
                            }`}>
                                <span className="w-2 h-2 rounded-full bg-green-400 animate-pulse"></span>
                                Mijn Team
                            </div>
                            <h1 className={`text-3xl sm:text-4xl font-black mb-2 leading-tight ${
                                isEsports ? 'text-transparent bg-clip-text bg-gradient-to-r from-white to-slate-100 drop-shadow-[0_0_8px_rgba(255,255,255,0.2)]' : ''
                            }`}>
                                {team?.name}
                            </h1>
                            <p className="flex items-center gap-2 opacity-90 text-sm font-medium mt-3">
                                <span className={`px-3 py-1 rounded-lg text-xs font-bold backdrop-blur-sm border ${
                                    isEsports ? 'bg-purple-950/40 border-purple-500/20 text-purple-300' : 'bg-white/20 border-white/10 text-white'
                                }`}>
                                    {team?.poule?.name || 'Geen Poule'}
                                </span>
                            </p>
                        </div>
                        <div className="flex flex-col items-end gap-3 shrink-0">
                            <button
                                onClick={() => setSelectedTeamId(null)}
                                className={`px-3 py-1.5 sm:px-4 sm:py-2 rounded-xl transition-all text-xs font-bold uppercase tracking-wide flex items-center gap-2 border hover:scale-105 active:scale-95 shadow-lg ${
                                    isEsports 
                                        ? 'bg-purple-600/40 hover:bg-purple-500/60 border-purple-400/30 text-white shadow-[0_0_15px_rgba(168,85,247,0.3)]' 
                                        : 'bg-white/20 border-white/20 hover:bg-white/30 text-white'
                                }`}
                                title="Kies een ander team"
                            >
                                <RefreshCw size={14} />
                                <span className="hidden sm:inline">Wijzig</span>
                            </button>
                            {team?.logo_url ? (
                                <img
                                    src={team.logo_url}
                                    alt={team.name}
                                    className={`w-16 h-16 sm:w-20 sm:h-20 object-contain rounded-xl p-1.5 shadow-xl drop-shadow-md transition-transform hover:scale-105 ${
                                        isEsports ? 'bg-[#12121e] border border-purple-500/20' : 'bg-white'
                                    }`}
                                />
                            ) : (
                                <div className={`w-16 h-16 sm:w-20 sm:h-20 rounded-xl flex items-center justify-center backdrop-blur-sm border shadow-xl ${
                                    isEsports ? 'bg-purple-900/30 border-purple-500/20' : 'bg-white/20 border-white/30'
                                }`}>
                                    <Shield size={32} className="text-white/70" />
                                </div>
                            )}
                        </div>
                    </div>
                </div>
            </div>

            {/* Next Match Card */}
            <div className={`rounded-2xl shadow-lg border overflow-hidden transition-all duration-300 ${
                isEsports 
                    ? 'bg-[#0a0a14]/90 border-purple-500/20' 
                    : 'bg-white/90 border-white/20'
            }`}>
                <div className={`p-3 font-bold flex items-center gap-2 border-b ${
                    isEsports 
                        ? 'bg-gradient-to-r from-purple-950 to-indigo-950 text-white border-purple-500/10' 
                        : 'bg-gray-900/90 text-white border-gray-100'
                }`}>
                    <Clock size={18} className={isEsports ? 'text-cyan-400' : 'text-orange-400'} /> Eerstvolgende Wedstrijd
                </div>
                {nextMatch ? (
                    <div className="p-6 text-center">
                        <div className={`flex justify-between items-center text-xl font-black mb-4 ${
                            isEsports ? 'text-white font-mono' : 'text-gray-900'
                        }`}>
                            <div className="flex-1 text-right">{nextMatch.home_team?.name}</div>
                            <div className={`px-4 text-sm font-bold ${isEsports ? 'text-cyan-400 font-mono' : 'text-gray-400'}`}>VS</div>
                            <div className="flex-1 text-left">{nextMatch.away_team?.name}</div>
                        </div>
                        {(() => {
                            const formatTime = (timeStr) => {
                                if (!timeStr) return '--:--';
                                if (timeStr.includes('T')) {
                                    try {
                                        const dateObj = new Date(timeStr);
                                        return dateObj.toLocaleTimeString('nl-NL', { hour: '2-digit', minute: '2-digit' });
                                    } catch (e) {
                                        return '--:--';
                                    }
                                }
                                return timeStr.slice(0, 5);
                            };
                            return (
                                <div className={`flex justify-center gap-4 text-sm font-medium py-2.5 rounded-lg mx-auto w-fit px-6 border ${
                                    isEsports 
                                        ? 'bg-purple-950/20 border-purple-500/20 text-purple-300 font-mono text-xs' 
                                        : 'bg-gray-50 border-gray-100 text-gray-600'
                                }`}>
                                    <span className="flex items-center gap-1.5"><Calendar size={14} className={isEsports ? 'text-cyan-400' : ''} /> {formatTime(nextMatch.start_time)}</span>
                                    <span className={`w-px h-4 ${isEsports ? 'bg-purple-500/20' : 'bg-gray-300'}`}></span>
                                    <span className="flex items-center gap-1.5"><MapPin size={14} className={isEsports ? 'text-cyan-400' : ''} /> {nextMatch.field || 'Veld?'}</span>
                                </div>
                            );
                        })()}
                    </div>
                ) : (
                    <div className={`p-10 text-center flex flex-col items-center justify-center gap-2 italic ${
                        isEsports ? 'text-slate-500 font-mono text-xs' : 'text-gray-500 text-sm'
                    }`}>
                        <Gamepad2 size={32} className={isEsports ? 'text-purple-500/30 mb-1 animate-bounce' : 'text-gray-300'} />
                        Geen komende wedstrijden gevonden. Toernooi afgelopen?
                    </div>
                )}
            </div>

            {/* Quick Actions */}
            <div className="grid grid-cols-2 gap-3 mb-3">
                <button 
                    onClick={() => navigate(`/live/${tournamentId}/matches`)} 
                    className={`p-4 rounded-xl shadow border text-left group transition-all duration-300 hover:scale-[1.02] ${
                        isEsports 
                            ? 'bg-[#0a0a14]/90 border-purple-500/20 text-white hover:bg-purple-500/5 hover:border-purple-500/40 shadow-[0_0_20px_rgba(168,85,247,0.02)]' 
                            : 'bg-white/90 border-white/20 text-gray-900 hover:bg-white'
                    }`}
                >
                    <Calendar size={24} className={`mb-2 group-hover:scale-110 transition-transform ${isEsports ? 'text-cyan-400' : 'text-orange-500'}`} />
                    <div className="font-bold text-sm">Volledig Schema</div>
                    <div className={`text-[10px] ${isEsports ? 'text-slate-400' : 'text-gray-500'}`}>Bekijk alle wedstrijden</div>
                </button>
                
                <button 
                    onClick={() => navigate(`/live/${tournamentId}/standings`)} 
                    className={`p-4 rounded-xl shadow border text-left group transition-all duration-300 hover:scale-[1.02] ${
                        isEsports 
                            ? 'bg-[#0a0a14]/90 border-purple-500/20 text-white hover:bg-purple-500/5 hover:border-purple-500/40 shadow-[0_0_20px_rgba(168,85,247,0.02)]' 
                            : 'bg-white/90 border-white/20 text-gray-900 hover:bg-white'
                    }`}
                >
                    <Trophy size={24} className={`mb-2 group-hover:scale-110 transition-transform ${isEsports ? 'text-fuchsia-400' : 'text-indigo-500'}`} />
                    <div className="font-bold text-sm">Bekijk Stand</div>
                    <div className={`text-[10px] ${isEsports ? 'text-slate-400' : 'text-gray-500'}`}>Huidige positie in poule</div>
                </button>
            </div>

            <div className="grid grid-cols-2 gap-3">
                <a 
                    href={`/api/calendar?teamId=${team?.id}`}
                    target="_blank"
                    rel="noopener noreferrer"
                    className={`p-4 rounded-xl shadow border text-left group transition-all duration-300 hover:scale-[1.02] block ${
                        isEsports 
                            ? 'bg-[#0a0a14]/90 border-purple-500/20 text-white hover:bg-purple-500/5 hover:border-purple-500/40 shadow-[0_0_20px_rgba(168,85,247,0.02)]' 
                            : 'bg-white/90 border-white/20 text-gray-900 hover:bg-white'
                    }`}
                >
                    <Calendar size={24} className={`mb-2 group-hover:scale-110 transition-transform ${isEsports ? 'text-green-400' : 'text-green-500'}`} />
                    <div className="font-bold text-sm">Zet in Agenda</div>
                    <div className={`text-[10px] ${isEsports ? 'text-slate-400' : 'text-gray-500'}`}>Synchroniseer via iCal</div>
                </a>

                {/* Push Notifications Toggle */}
                {isSupported && (
                    <button 
                        onClick={() => !isSubscribed && subscribe(tournamentId, team?.id)}
                        disabled={isSubscribed || pushLoading}
                        className={`p-4 rounded-xl shadow border text-left group transition-all duration-300 ${
                            isSubscribed
                                ? (isEsports ? 'bg-purple-900/50 border-purple-500/50 text-purple-200' : 'bg-green-50 border-green-200 text-green-800')
                                : (isEsports 
                                    ? 'bg-[#0a0a14]/90 border-purple-500/20 text-white hover:bg-purple-500/5 hover:border-purple-500/40 hover:scale-[1.02]' 
                                    : 'bg-white/90 border-white/20 text-gray-900 hover:bg-white hover:scale-[1.02]')
                        }`}
                    >
                        {isSubscribed ? (
                            <BellRing size={24} className={`mb-2 ${isEsports ? 'text-purple-400' : 'text-green-600'}`} />
                        ) : (
                            <Bell size={24} className={`mb-2 group-hover:scale-110 transition-transform ${isEsports ? 'text-pink-400' : 'text-pink-500'}`} />
                        )}
                        <div className="font-bold text-sm">{isSubscribed ? 'Meldingen Aan' : 'Zet Meldingen Aan'}</div>
                        <div className={`text-[10px] ${isEsports ? 'text-slate-400' : 'text-gray-500'}`}>
                            {isSubscribed ? 'Je ontvangt updates' : 'Krijg een seintje voor de start'}
                        </div>
                    </button>
                )}
            </div>

            {/* Last Result */}
            {lastMatch && (
                <div className={`backdrop-blur rounded-xl border p-4 transition-all duration-300 ${
                    isEsports 
                        ? 'bg-[#0a0a14]/90 border-purple-500/20 text-white shadow-[0_0_20px_rgba(168,85,247,0.02)]' 
                        : 'bg-white/80 border-white/20 text-gray-800'
                }`}>
                    <h3 className={`text-xs font-bold uppercase tracking-wider mb-3 font-mono ${isEsports ? 'text-purple-400' : 'text-gray-500'}`}>Laatste Resultaat</h3>
                    <div className="flex items-center justify-between font-bold text-sm">
                        <span className="flex-1 text-right truncate pr-4">{lastMatch.home_team?.name}</span>
                        <span className={`px-3 py-1 rounded font-mono font-black border text-center min-w-[3.5rem] ${
                            isEsports 
                                ? 'bg-purple-950 border-purple-500/30 text-cyan-400 drop-shadow-[0_0_5px_rgba(6,182,212,0.4)]' 
                                : 'bg-gray-900 text-white'
                        }`}>{lastMatch.score_home} - {lastMatch.score_away}</span>
                        <span className="flex-1 text-left truncate pl-4">{lastMatch.away_team?.name}</span>
                    </div>
                </div>
            )}
            {/* Player Roster & Biometrics Section */}
            {teamData.players && teamData.players.length > 0 && (
                <div className="space-y-4">
                    <div className="flex justify-between items-center border-b border-purple-500/10 pb-2">
                        <h3 className="text-sm font-black uppercase tracking-wider text-purple-400 font-mono flex items-center gap-2">
                            <Activity size={16} className="text-cyan-400" />
                            Speler Roster & Biometrische Spelerskaarten
                        </h3>
                    </div>
                    
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                        {teamData.players.map(player => {
                            const speed = player.reaction_speed || 215;
                            const bio = player.biometrics || { mechanical: 75, speed: 75, brain: 75, focus: 75, chemistry: 75 };
                            
                            // Reflex Tier calculation
                            let reflexTier = 'Gemiddeld';
                            let tierColor = 'text-slate-400 border-slate-500/20 bg-slate-950/40';
                            if (speed < 185) {
                                reflexTier = 'S-Tier (Godlike)';
                                tierColor = 'text-cyan-400 border-cyan-400/30 bg-cyan-950/20 shadow-[0_0_10px_rgba(6,182,212,0.15)]';
                            } else if (speed < 210) {
                                reflexTier = 'A-Tier (Pro)';
                                tierColor = 'text-purple-400 border-purple-400/30 bg-purple-950/20';
                            } else if (speed < 240) {
                                reflexTier = 'B-Tier (Voldoende)';
                                tierColor = 'text-yellow-400 border-yellow-400/30 bg-yellow-950/20';
                            }

                            return (
                                <div 
                                    key={player.id} 
                                    className="p-5 rounded-2xl border border-purple-500/20 bg-[#0a0a14]/90 relative overflow-hidden shadow-[0_0_20px_rgba(168,85,247,0.02)] transition-all duration-300 hover:border-purple-500/40 hover:scale-[1.01]"
                                >
                                    {/* Top Line */}
                                    <div className="flex justify-between items-start mb-3.5">
                                        <div>
                                            <h4 className="font-bold text-white tracking-wide text-sm flex items-center gap-1.5">
                                                <Shirt size={14} className="text-purple-400" />
                                                {player.name}
                                            </h4>
                                            <span className="text-[10px] font-mono text-slate-400 uppercase">Gamer Profile</span>
                                        </div>
                                        <div className={`text-[10px] font-mono font-bold px-2 py-0.5 rounded-full border ${tierColor}`}>
                                            {reflexTier}
                                        </div>
                                    </div>

                                    {/* Reaction speed bar */}
                                    <div className="bg-purple-950/25 border border-purple-500/10 p-3 rounded-xl mb-4 flex items-center justify-between">
                                        <span className="text-[10px] font-mono text-slate-400 uppercase flex items-center gap-1">
                                            <Zap size={12} className="text-cyan-400" />
                                            Reactiesnelheid:
                                        </span>
                                        <span className="text-xs font-black font-mono text-cyan-400 drop-shadow-[0_0_5px_rgba(6,182,212,0.4)]">
                                            {speed} ms
                                        </span>
                                    </div>

                                    {/* Mini Biometrics Grid */}
                                    <div className="space-y-2 text-[11px]">
                                        <div>
                                            <div className="flex justify-between font-mono text-slate-400 mb-0.5">
                                                <span>Mechanical Precision</span>
                                                <span className="text-slate-200">{bio.mechanical}%</span>
                                            </div>
                                            <div className="w-full bg-purple-950/40 rounded-full h-1.5 overflow-hidden">
                                                <div 
                                                    className="bg-cyan-500 h-full rounded-full" 
                                                    style={{ width: `${bio.mechanical}%` }}
                                                />
                                            </div>
                                        </div>

                                        <div>
                                            <div className="flex justify-between font-mono text-slate-400 mb-0.5">
                                                <span>Movement Snelheid</span>
                                                <span className="text-slate-200">{bio.speed}%</span>
                                            </div>
                                            <div className="w-full bg-purple-950/40 rounded-full h-1.5 overflow-hidden">
                                                <div 
                                                    className="bg-purple-500 h-full rounded-full" 
                                                    style={{ width: `${bio.speed}%` }}
                                                />
                                            </div>
                                        </div>

                                        <div>
                                            <div className="flex justify-between font-mono text-slate-400 mb-0.5">
                                                <span>Cognitieve Focus</span>
                                                <span className="text-slate-200">{bio.focus}%</span>
                                            </div>
                                            <div className="w-full bg-purple-950/40 rounded-full h-1.5 overflow-hidden">
                                                <div 
                                                    className="bg-yellow-500 h-full rounded-full" 
                                                    style={{ width: `${bio.focus}%` }}
                                                />
                                            </div>
                                        </div>
                                    </div>

                                </div>
                            );
                        })}
                    </div>
                </div>
            )}

        </div>
    );
};

export default PublicMyTeamPage;
